import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import CreateProduct from './CreateProduct'
import AddSeikoWatch from './AddSeikoWatch'
import PrintOtherTags from './PrintOtherTags'
import Tags from './Tags'
import Page from '../../components/Page'
import Lightbox from '../../components/Lightbox'
import DataTable from '../../components/DataTable'
import PageTitle from '../../components/PageTitle'
import useFetch from '../../hooks/useFetch'
import { presets } from './productPresets'
import toEditProduct from './toEditProduct'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import PrintIcon from '@mui/icons-material/Print'
import ProductSheet from './ProductSheet'
import BarcodeScanner, { isCameraAvailable } from '../../components/BarcodeScanner'
import { useReactToPrint } from 'react-to-print'

// The server accepts up to 250 (Shopify's own cap), but the free MUI DataGrid refuses
// a pageSize above 100 — 250 is a DataGridPro feature.
const PAGE_SIZES = [50, 100]

export default function Products(props) {
    const { t } = useTranslation()
    const { setAuth, vendors } = props
    const [showCreateProduct, setShowCreateProduct] = useState(false)
    const [showAddSeiko, setShowAddSeiko] = useState(false)
    const [showImage, setShowImage] = useState(false)
    const [showPrintOtherTags, setShowPrintOtherTags] = useState(false)
    const [image, setImage] = useState(null)
    const [targetBarcodes, setTargetBarcodes] = useState([])
    const [tagInfo, setTagInfo] = useState({ products: [], resolve: undefined })
    const [edit, setEdit] = useState(false)
    const [manageProductTitle, setManageProductTitle] = useState('')
    // The search field, and what a lookup found. `found` being set is what opens
    // the product sheet -- the screen a scan lands on.
    const [query, setQuery] = useState('')
    const [found, setFound] = useState(null)
    const [lookupError, setLookupError] = useState(null)
    const [showScanner, setShowScanner] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState(null)
    // Picking rows is a mode you enter deliberately, not a set of checkboxes
    // sitting there waiting to be misread as part of the list.
    const [selectMode, setSelectMode] = useState(false)
    const mainTagsRef = useRef()
    const [data, setData] = useState([])
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    // Shopify pages by opaque cursor, not offset, so a page is only addressable once
    // we have walked to it. cursors[n] is the cursor that opens page n; page 0 has none.
    const [cursors, setCursors] = useState([null])
    const [pageInfo, setPageInfo] = useState({ hasNextPage: false, totalCount: null })
    // The last page index that actually loaded — where a failed page falls back to.
    const loadedPage = useRef(0)
    // `page|pageSize` of the most recent request, so the effect can tell its own
    // trigger apart from a reset that a manual refresh has already served.
    const lastRequested = useRef(null)
    // A refresh runs outside the paging effect, so it cannot lean on that effect's
    // cleanup to cancel itself. This is how it knows the page has gone.
    const mounted = useRef(true)
    const [product, setProduct] = useState({
        id: '',
        barcode: '',
        title: '',
        category: '',
        price: '',
        nwk_quantity: 0,
        lb_quantity: 0,
        vendor: '',
        weight: 0
    })

    const { fetchResource, isPending } = useFetch({ setAuth });

    let hasSelected = targetBarcodes.length > 0

    const renderImageCell = params =>
        params.row.image ? (
            <Box
                component='img'
                src={params.row.image}
                onClick={event => {
                    // The row underneath opens the product. Tapping the photo
                    // means "show me this bigger", so it must not do both -- the
                    // sheet would cover the picture it just opened.
                    event.stopPropagation()
                    handleImageClick(params.row.image)
                }}
                alt='product'
                // Width alone let a portrait photo render 87x181 inside a 100px
                // row, spilling over the dividers above and below it. Bound both
                // sides and crop, rather than letting the image decide the row.
                sx={{
                    width: 87,
                    height: 87,
                    objectFit: 'cover',
                    borderRadius: '8px',
                    cursor: 'pointer'
                }}
            />
        ) : (
            <Typography width='100%' align='center' variant='caption' color='text.secondary'>
                {t('products.noImage')}
            </Typography>
        )

    // A product, on a phone. Square thumbnail, the title with room to wrap, and
    // the two things staff look a product up by underneath it.
    const renderProductCard = row => (
        <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <Box
                component={row.image ? 'img' : 'div'}
                src={row.image || undefined}
                alt={row.image ? 'product' : undefined}
                onClick={row.image ? event => {
                    event.stopPropagation()
                    handleImageClick(row.image)
                } : undefined}
                sx={{
                    width: 72,
                    height: 72,
                    flex: 'none',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    bgcolor: 'action.hover',
                    cursor: row.image ? 'pointer' : 'default'
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Two lines. The grid gave this 108px -- about thirteen
                    characters -- and a product title carries the words that tell
                    two of them apart at the end. */}
                <Typography
                    data-testid='product-card-title'
                    sx={{
                        fontWeight: 500,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}
                >
                    {row.title}
                </Typography>
                <Typography variant='body2' color='text.secondary' noWrap>{row.category}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', mt: 0.5 }}>
                    <Typography sx={{ fontWeight: 600 }}>{row.price}</Typography>
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                        {row.id}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )

    const mobileColumns = [
        {
            field: 'image',
            headerName: t('products.columns.image'),
            width: 108,
            renderCell: params => renderImageCell(params)
        },
        {
            field: 'title',
            headerName: t('products.columns.title'),
            width: 180,
            flex: 1
        },
        {
            headerName: t('products.columns.barcode'),
            field: 'id',
            width: 90
        }
    ]

    const desktopColumns = [
        {
            field: 'image',
            headerName: t('products.columns.image'),
            width: 108,
            renderCell: params => renderImageCell(params)
        },
        {
            field: 'title',
            headerName: t('products.columns.title'),
            width: 180,
            flex: 1
        },
        {
            field: 'category',
            headerName: t('products.columns.category'),
            width: 180
        },
        {
            field: 'price',
            headerName: t('products.columns.price'),
            minWidth: 100
        },
        {
            field: 'metal',
            headerName: t('products.columns.metal'),
            width: 70
        },
        {
            headerName: t('products.columns.barcode'),
            field: 'id',
            width: 90
        }
    ]

    let rows = (data || []).map(product => {
        const { barcode, title, body_html, category, price, image, metafields, vendor, weight } = product

        if (title !== 'Gift card')
            return {
                id: barcode,
                title: title,
                body_html: body_html || '',
                category: category,
                price: `$${price}`,
                image: image,
                metal: metafields.metal?.value || '',
                vendor: vendor,
                weight: weight
            }
    })

    rows = rows.filter(item => item?.id)

    // The footer's total. Deriving the tail from hasNextPage rather than trusting the
    // count outright matters: the count comes from a separate REST call, and if it ever
    // overshoots what the cursor walk actually yields, the grid would offer a next page
    // we have no cursor for and silently serve page 1's products in its place.
    const rowCount = pageInfo.hasNextPage
        ? Math.max(pageInfo.totalCount || 0, (page + 1) * pageSize + 1)
        // A page that rendered nothing — every product on it filtered out, or a tail
        // page emptied since its cursor was minted — still has to count as a page of
        // its own. Otherwise the grid clamps itself to an earlier index without telling
        // us, and Previous skips straight past the page the user is on.
        : page * pageSize + (page > 0 ? Math.max(rows.length, 1) : rows.length)

    async function handleAddProduct() {
        setEdit(false)
        setProduct({})
        setManageProductTitle(t('products.addProduct'))
        setShowCreateProduct(true)
    }

    // What the pencil's empty state used to do, given a name and a home. The
    // response shape is get-product's, which is what ProductSheet reads and what
    // openProductForEdit turns into the form's fields.
    async function lookUp(barcode) {
        const code = String(barcode || '').trim()
        if (!code) return

        setLookupError(null)
        const response = await fetchResource({
            resource: 'products/get-product',
            body: { barcode: code },
            loadingKey: 'lookup'
        })

        if (response.error) {
            return setLookupError(t('products.notFound', { barcode: code }))
        }

        setFound({ ...response.product, barcode: code })
    }

    function handleLookup() {
        lookUp(query)
    }

    // A decode fills the field, fires the lookup and opens the product in one
    // motion -- no submit, no extra tap.
    function handleScan(code) {
        setQuery(code)
        setShowScanner(false)
        lookUp(code)
    }

    function handleEditFound(product) {
        setProduct(toEditProduct(product))
        setManageProductTitle(t('products.editProduct'))
        setEdit(true)
        setFound(null)
        setQuery('')
        setShowCreateProduct(true)
    }

    // Asks the backend for the tag rather than reading it out of `data`: a
    // product found by barcode is very often not on the page currently loaded.
    async function handlePrintFound(product) {
        const response = await fetchResource({
            resource: 'products/get-tag-info',
            body: { barcodes: [product.barcode] },
            blocking: true
        })

        if (response.error) return alert(response.error)
        setTagInfo({ products: response.products, resolve: undefined })
        setFound(null)
        printTags()
    }

    function handlePrintSelected() {
        printTags()
    }

    function handleImageClick(image) {
        setImage(image)
        setShowImage(true)
    }

    function handleSelectChange(e) {
        setTargetBarcodes(e)
    }

    function getTagInfo(resolve) {
        const selectedProducts = data.filter(product => targetBarcodes.includes(product.barcode))
        const newTagInfos = []

        selectedProducts.map(product => {
            const { metafields } = product
            const newTagInfo = {}

            if (metafields.gemstones?.value.length > 0) {
                let formattedGemstones = metafields.gemstones.value.map(gemstone => {
                    const gemstoneWords = gemstone.split(' ')
                    const lowerCaseGemstoneWords = gemstoneWords.map(word => {
                        return word.slice(0, 1).toLowerCase() + word.slice(1)
                    })
                    return presets.gemstones[lowerCaseGemstoneWords.join('_')]
                })

                if (formattedGemstones.length > 3) {
                    formattedGemstones = formattedGemstones.splice(0, 3)
                    formattedGemstones[2] = formattedGemstones[2] + '...'
                }

                newTagInfo.gemstones = formattedGemstones.join(', ')
            }

            if (metafields.center_stone?.value || metafields.side_stones?.value) {
                Object.assign(newTagInfo, {
                    center_stone: metafields.center_stone?.value || 0,
                    side_stones: metafields.side_stones?.value || 0
                })
            }

            if (metafields.band_width?.value) {
                Object.assign(newTagInfo, {
                    band_width: metafields.band_width.value
                })
            }

            if (metafields.length?.value?.value) {
                Object.assign(newTagInfo, {
                    length: metafields.length.value.value
                })
            }

            if (metafields.ring_size?.value) {
                Object.assign(newTagInfo, {
                    ring_size: metafields.ring_size.value
                })
            }

            if (!product.category.includes('Watches')) {
                Object.assign(newTagInfo, {
                    barcode: product.barcode,
                    metal: presets.metals[metafields.metal.value],
                    price: '$' + product.price.split('.')[0],
                    chainLength: metafields.length?.value + ' in.' || null
                })
            }

            newTagInfos.push(newTagInfo)
        })
        setTagInfo({ products: newTagInfos, resolve })
    }

    useEffect(() => {
        if (tagInfo.resolve) tagInfo.resolve()
    }, [tagInfo])

    // The hook rather than the <ReactToPrint> component, for the same reason
    // PrintOtherTags moved: the component hands its button back through a render
    // prop, so the print control could never be an ordinary button in an ordinary
    // row. onBeforeGetContent only runs for the selection path; printing a single
    // found tag has already fetched its payload by the time it gets here.
    const printTags = useReactToPrint({
        content: () => mainTagsRef.current,
        onBeforeGetContent: () =>
            targetBarcodes.length
                ? new Promise(resolve => { getTagInfo(resolve) })
                : Promise.resolve()
    })

    // The one place a page of products is fetched. The effect below drives it when the
    // user pages around; refreshProducts drives it directly after a write. It takes the
    // page, size and cursor table as arguments rather than reading state, because a
    // refresh has to fetch before React has committed the resets that accompany it.
    async function loadProducts(targetPage, targetPageSize, cursorTable, isCancelled = () => false) {
        lastRequested.current = `${targetPage}|${targetPageSize}`

        // A page is only reachable through the cursor that opens it. If that cursor
        // is missing — the page before it failed, or answered without one — asking
        // anyway fetches page one and labels it page N, and every Previous from
        // there walks a corrupted table.
        if (targetPage > 0 && !cursorTable[targetPage]) {
            setPage(loadedPage.current)
            return
        }

        try {
            const cursor = cursorTable[targetPage]
            const query = cursor
                ? `?limit=${targetPageSize}&cursor=${encodeURIComponent(cursor)}`
                : `?limit=${targetPageSize}`
            const response = await fetchResource({ method: 'GET', resource: `products${query}`, loadingKey: 'products' });
            // A response for a page the user has already left must not overwrite what
            // they are looking at, nor alert them about a request they abandoned.
            if (isCancelled()) return;
            if (response.error) throw new Error(response.error);

            loadedPage.current = targetPage
            setData(response.products);

            const { hasNextPage = false, endCursor = null, totalCount = null } = response.pageInfo || {};
            setPageInfo({ hasNextPage, totalCount });

            if (endCursor) {
                // Remember how to reach the following page, and drop anything
                // recorded beyond it — those cursors are no longer reachable from here.
                setCursors(prev => {
                    if (prev[targetPage + 1] === endCursor) return prev;
                    const next = prev.slice(0, targetPage + 1);
                    next[targetPage + 1] = endCursor;
                    return next;
                });
            }
        } catch (error) {
            if (isCancelled()) return;
            // Drop back to the last page we actually hold. Staying on a page number
            // whose cursor was never recorded leaves the grid offering a Next we
            // cannot honour.
            setPage(loadedPage.current)
            alert(t('products.fetchListError', { message: error.message }));
        }
    }

    // Refetch after a write, and for the manual refresh control. Both used to be
    // window.location.reload — a whole SPA reboot, white screen and all, to update
    // one list.
    //
    // Always page one: the server sorts UPDATED_AT descending, so anything just
    // saved is now the first row. Refetching the page the user was on would show
    // them a list their product has already left, shifted by one. The cursors go
    // with it — they addressed an order that no longer holds.
    async function refreshProducts() {
        setTargetBarcodes([])
        setCursors([null])
        loadedPage.current = 0
        setPage(0)
        await loadProducts(0, pageSize, [null], () => !mounted.current)
    }

    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    useEffect(() => {
        // refreshProducts fetches page one itself and then resets `page`, which lands
        // back here. Without this, every refresh from a later page costs a second,
        // identical request — one that settles after the blocking overlay has closed.
        if (lastRequested.current === `${page}|${pageSize}`) return

        let cancelled = false
        loadProducts(page, pageSize, cursors, () => cancelled)

        return () => { cancelled = true };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [page, pageSize]);

    function handlePageChange(newPage) {
        // Edit and Print look their products up in `data`, which now holds only the
        // page on screen. A barcode carried over from the page we are leaving is not
        // in there any more, so it has to go.
        setTargetBarcodes([])
        setPage(newPage)
    }

    function handlePageSizeChange(newPageSize) {
        // Cursors from a 50-per-page walk do not address a 100-per-page list.
        setTargetBarcodes([])
        setCursors([null])
        loadedPage.current = 0
        setPage(0)
        setPageSize(newPageSize)
    }

    // The product form takes the whole screen instead of floating over the list.
    // Rendered in place of it rather than beside it: FormPage sizes itself to the
    // viewport, and nesting that inside the list's grid would put a full-height
    // form inside a scrolling row. Products itself stays mounted either way, so
    // page, cursors and selection all survive and Save comes back to the page the
    // product was on.
    if (showAddSeiko) {
        return (
            <AddSeikoWatch
                showAddSeiko={showAddSeiko}
                setShowAddSeiko={setShowAddSeiko}
                fetchResource={fetchResource}
                refreshProducts={refreshProducts}
                // Intake refuses to create a second listing for a model already in stock.
                // Rather than tell the clerk to go and find it, hand the barcode back here
                // -- this page already knows how to fetch a piece and offer Edit and Print,
                // and Edit is how a second unit actually gets added: as a quantity.
                onExistingPiece={barcode => { setShowAddSeiko(false); lookUp(barcode) }}
            />
        )
    }

    if (showCreateProduct) {
        return (
            <CreateProduct
                setShowCreateProduct={setShowCreateProduct}
                showCreateProduct={showCreateProduct}
                setTargetBarcodes={setTargetBarcodes}
                fetchResource={fetchResource}
                refreshProducts={refreshProducts}
                edit={edit}
                product={edit ? product : null}
                manageProductTitle={manageProductTitle}
                vendors={vendors}
            />
        )
    }

    return (
        <Page>
            {/* PageTitle rather than Page's own title slot: that slot sits outside
                the body container, so on a monitor it lands hard left of the
                content it names while every other page centers its title. */}
            <PageTitle>{t('products.title')}</PageTitle>
            {/* Four mystery icons become one field, one primary action and a
                menu that says what each thing does in words. The pencil and the
                printer each did two different jobs -- lookup versus edit, and
                print-a-list versus print-the-selection -- with nothing on screen
                to say which one a tap would get. */}
            <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                    size='small'
                    fullWidth
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    onKeyDown={event => { if (event.key === 'Enter') handleLookup() }}
                    label={t('products.searchOrScan')}
                    disabled={isPending('lookup')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' />
                            </InputAdornment>
                        ),
                        // Scanning is the submit: the camera lives inside the
                        // field, and a decode fills it, fires the lookup and opens
                        // the product in one motion. Typing is the fallback for a
                        // damaged tag, and there Enter submits.
                        endAdornment: isCameraAvailable() ? (
                            <InputAdornment position='end'>
                                <IconButton
                                    aria-label={t('common.scan')}
                                    onClick={() => setShowScanner(true)}
                                    edge='end'
                                >
                                    <QrCodeScannerIcon />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }}
                />
                <IconButton
                    aria-label={t('products.addProduct')}
                    onClick={handleAddProduct}
                    sx={{ flex: 'none', width: 44, height: 44 }}
                >
                    <AddCircleIcon />
                </IconButton>
                <IconButton
                    aria-label={t('products.moreActions')}
                    onClick={event => setMenuAnchor(event.currentTarget)}
                    sx={{ flex: 'none', width: 44, height: 44 }}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
            >
                <MenuItem onClick={() => { setMenuAnchor(null); setSelectMode(true) }}>
                    {t('products.selectItems')}
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); setShowPrintOtherTags(true) }}>
                    {t('products.printTagsByBarcode')}
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); setShowAddSeiko(true) }}>
                    {t('products.seiko.addSeikoWatch')}
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); refreshProducts() }}>
                    {t('products.refresh')}
                </MenuItem>
            </Menu>

            {lookupError && (
                <Alert severity='error' sx={{ flex: 'none', mb: 1 }} onClose={() => setLookupError(null)}>
                    {lookupError}
                </Alert>
            )}

            {/* Only while the mode is on. Printing the tags for rows you picked is
                its own task, so it gets its own mode and a button that says how
                many tags it is about to send to the printer. */}
            {selectMode && (
                <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Button
                        variant='contained'
                        startIcon={<PrintIcon />}
                        disabled={!hasSelected}
                        onClick={handlePrintSelected}
                        sx={{ flex: 1 }}
                    >
                        {t('products.printSelectedTags', { count: targetBarcodes.length })}
                    </Button>
                    <Button
                        variant='text'
                        color='inherit'
                        onClick={() => { setSelectMode(false); setTargetBarcodes([]) }}
                    >
                        {t('products.doneSelecting')}
                    </Button>
                </Box>
            )}

            <div style={{ display: 'none' }}>
                <Tags products={tagInfo.products} ref={mainTagsRef} />
            </div>

            {showPrintOtherTags && (
                <PrintOtherTags
                    showPrintOtherTags={showPrintOtherTags}
                    setShowPrintOtherTags={setShowPrintOtherTags}
                    fetchResource={fetchResource}
                />
            )}

            <ProductSheet
                open={isPending('lookup') || Boolean(found)}
                loading={isPending('lookup')}
                onClose={() => setFound(null)}
                product={found}
                onImageClick={handleImageClick}
                onEdit={handleEditFound}
                onPrint={handlePrintFound}
            />

            {showScanner && (
                <BarcodeScanner
                    open={showScanner}
                    onClose={() => setShowScanner(false)}
                    onScan={handleScan}
                    title={t('products.scanItemBarcode')}
                />
            )}

            <DataTable
                checkboxSelection={selectMode}
                // A tap can only mean one thing at a time. Out of select mode it
                // opens the product -- which is how you edit one now that the
                // pencil is gone; in select mode it picks the row, which is a
                // larger target than the checkbox beside it.
                onRowClick={selectMode ? null : row => lookUp(row.id)}
                disableSelectionOnClick={!selectMode}
                // The table picks one row at a time by default, which is what
                // Users and Vendors want. Here the mode exists to print a batch,
                // so a second pick has to add to the first rather than replace it.
                disableMultipleSelection={false}
                rowHeight={100}
                loading={isPending('products')}
                desktopColumns={desktopColumns}
                mobileColumns={mobileColumns}
                renderCard={renderProductCard}
                rows={rows}
                handleSelectChange={handleSelectChange}
                // Row ids are barcodes, so the selection Products holds is the
                // selection model. Controlled because this grid is rebuilt every
                // time the product form closes.
                selectionModel={targetBarcodes}
                paginationMode='server'
                page={page}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageOptions={PAGE_SIZES}
                rowCount={rowCount}
            />
            <Lightbox
                open={showImage}
                onClose={() => setShowImage(false)}
                src={image}
                alt='product'
            />
        </Page>
    )
}
